import React, { useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import Clickable from 'components/Common/Clickable'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { merchandiseMegaMenuBreakpoints } from './merchandiseMegaMenuBreakpoints'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  padding: ${rem(24)};
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  width: 100%;
`

const LettersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: ${rem(8)};
  width: 100%;

  ${merchandiseMegaMenuBreakpoints.ultraWide} {
    grid-template-columns: repeat(7, 1fr);
  }

  ${merchandiseMegaMenuBreakpoints.mediumLarge} {
    grid-template-columns: repeat(5, 1fr);
  }
`

const LetterButton = styled(Clickable)`
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  padding: ${rem(10)} ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.default.four};
  border-radius: ${rem(2)};
  background: ${props => props.theme.palette.neutral.default.eight};
  transition: background-color 0.2s, border-color 0.2s;

  ${merchandiseMegaMenuBreakpoints.compact} {
    padding: ${rem(6)} ${rem(8)};
    font-size: ${rem(12)};
  }

  &.selected {
    background: ${props => props.theme.palette.neutral.default.seven};
    border-color: ${props => props.theme.palette.neutral.default.one};
  }

  ${mediaHoverable} {
    &:hover {
      background: ${props => props.theme.palette.neutral.default.seven};
    }
  }
`

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#'.split('')

interface Props {
  selectedLetter?: string
  onLetterSelect: (letter: string) => void
  onClose: () => void
}

function MerchandiseBrandsAlphabet(props: Props) {
  const {
    selectedLetter,
    onClose,
  } = props

  const handleLetterClick = useCallback(() => {
    onClose()
  }, [onClose])

  return <Container>
    <Header>
      <BodyText variant="medium" colour="neutral-two">
        Brands A-Z
      </BodyText>
    </Header>
    <LettersGrid>
      {ALPHABET.map(letter => (
        <LetterButton
          key={letter}
          onClick={handleLetterClick}
          className={selectedLetter === letter ? 'selected' : ''}
          to={`/shop/brands#${letter}`}
        >
          <BodyText variant="medium" colour="neutral-one">
            {letter}
          </BodyText>
        </LetterButton>
      ))}
    </LettersGrid>
    <TextLink
      to="/shop/brands"
      onClick={onClose}
      colour="neutral-two"
      size="medium"
      weight="regular"
    >
      View All Brands
    </TextLink>
  </Container>
}

export default MerchandiseBrandsAlphabet
